/**
 * Recurring templates data hook for the Accounting Recurring page.
 *
 * Returns the list of templates in the QueryResult<T> shape used
 * across Novala (data, loading, error, refetch). Also exposes
 * mutation helpers (updateTemplate, deleteTemplate, runNow,
 * skipNext, togglePause, endTemplate, createTemplate) so components
 * can patch the list optimistically.
 *
 * Backend swap: replace the useState initializer with a fetch (or
 * TanStack Query useQuery), replace each mutation function body
 * with a POST/PATCH/DELETE call. Nothing in the UI changes.
 *
 * Scope note: in PR 10 the state lives inside this hook, so
 * multiple components calling useRecurring get independent copies.
 * The Recurring page composes a single tree from the top
 * (index.tsx calls useRecurring once and passes data + mutations
 * down), so this is intentional and matches how Rules, Chart of
 * accounts, and Bank transactions already work. A shared Recurring
 * Context can be added in a follow-up PR if needed.
 */

import { useCallback, useEffect, useState } from "react";
import type {
  RecurringTemplate,
  RecurringStatus,
} from "../pages/Accounting/Recurring/recurringTypes";
import { MOCK_RECURRING_TEMPLATES } from "../pages/Accounting/Recurring/recurringMockData";

/** Standard Novala query-result shape (TanStack Query compatible). */
export interface QueryResult<T> {
  data: T;
  loading: boolean;
  error: Error | null;
  refetch: () => void;
}

/** Return shape adds mutation helpers on top of QueryResult. */
export interface UseRecurringResult extends QueryResult<RecurringTemplate[]> {
  /** Patch a template (name, mode, fields, schedule). Used by Edit flow. */
  updateTemplate: (id: string, patch: Partial<RecurringTemplate>) => void;

  /** Remove a template. Called after the Delete confirmation modal. */
  deleteTemplate: (id: string) => void;

  /**
   * Flip status between active and paused. Does not change the
   * nextDate; a resumed template picks up where it left off.
   */
  togglePause: (id: string) => void;

  /**
   * Fire the recurring once right now, independent of the schedule.
   * In mock mode: increments runCount, sets previousDate to today,
   * leaves nextDate unchanged. In backend: creates an actual
   * transaction in the ledger with today's date.
   */
  runNow: (id: string) => void;

  /**
   * Push the nextDate to the one after. In mock mode: calls into
   * the schedule to compute the following occurrence and swaps it
   * in. Used by the "Skip next occurrence" row action.
   */
  skipNext: (id: string) => void;

  /**
   * End a recurring. Sets status to "ended", nextDate to null.
   * Keeps the row and run history for audit. Used by "End this
   * recurring" row action.
   */
  endTemplate: (id: string) => void;

  /**
   * Create a new template. Used by the Builder drawer (PR 11).
   * Assigns an id and timestamps; returns the created template.
   */
  createTemplate: (template: Omit<RecurringTemplate, "id" | "createdAt" | "updatedAt" | "runCount">) => RecurringTemplate;
}

/**
 * Simulated network latency so the loading state is visible in
 * dev. Set to 0 to disable. Backend swap replaces this entirely
 * with real request timing.
 */
const SIMULATED_LATENCY_MS = 80;

export function useRecurring(): UseRecurringResult {
  const [data, setData] = useState<RecurringTemplate[]>(() => [
    ...MOCK_RECURRING_TEMPLATES,
  ]);
  const [loading, setLoading] = useState(false);
  const [error] = useState<Error | null>(null);

  // Initial load simulation.
  useEffect(() => {
    setLoading(true);
    const timer = window.setTimeout(() => setLoading(false), SIMULATED_LATENCY_MS);
    return () => window.clearTimeout(timer);
  }, []);

  const refetch = useCallback(() => {
    setLoading(true);
    window.setTimeout(() => {
      setData([...MOCK_RECURRING_TEMPLATES]);
      setLoading(false);
    }, SIMULATED_LATENCY_MS);
  }, []);

  const updateTemplate = useCallback(
    (id: string, patch: Partial<RecurringTemplate>) => {
      setData((prev) =>
        prev.map((t) =>
          t.id === id
            ? { ...t, ...patch, updatedAt: new Date().toISOString() }
            : t,
        ),
      );
    },
    [],
  );

  const deleteTemplate = useCallback((id: string) => {
    setData((prev) => prev.filter((t) => t.id !== id));
  }, []);

  const togglePause = useCallback((id: string) => {
    setData((prev) =>
      prev.map((t) => {
        if (t.id !== id) return t;
        const nextStatus: RecurringStatus =
          t.status === "active" ? "paused" : "active";
        return {
          ...t,
          status: nextStatus,
          updatedAt: new Date().toISOString(),
        };
      }),
    );
  }, []);

  const runNow = useCallback((id: string) => {
    const today = new Date().toISOString().slice(0, 10);
    setData((prev) =>
      prev.map((t) =>
        t.id === id
          ? {
              ...t,
              previousDate: today,
              runCount: t.runCount + 1,
              updatedAt: new Date().toISOString(),
            }
          : t,
      ),
    );
  }, []);

  /**
   * Advance nextDate to the occurrence after it, based on frequency.
   * Simplified: adds one interval to the current nextDate.
   */
  const skipNext = useCallback((id: string) => {
    setData((prev) =>
      prev.map((t) => {
        if (t.id !== id || !t.nextDate) return t;
        const d = new Date(t.nextDate);
        switch (t.schedule.frequency) {
          case "daily":
            d.setDate(d.getDate() + 1);
            break;
          case "weekly":
            d.setDate(d.getDate() + 7);
            break;
          case "biweekly":
            d.setDate(d.getDate() + 14);
            break;
          case "monthly":
            d.setMonth(d.getMonth() + 1);
            break;
          case "quarterly":
            d.setMonth(d.getMonth() + 3);
            break;
          case "semi_annually":
            d.setMonth(d.getMonth() + 6);
            break;
          case "annually":
            d.setFullYear(d.getFullYear() + 1);
            break;
          case "custom":
            d.setDate(d.getDate() + 1);
            break;
        }
        return {
          ...t,
          nextDate: d.toISOString().slice(0, 10),
          updatedAt: new Date().toISOString(),
        };
      }),
    );
  }, []);

  const endTemplate = useCallback((id: string) => {
    setData((prev) =>
      prev.map((t) =>
        t.id === id
          ? {
              ...t,
              status: "ended" as RecurringStatus,
              nextDate: null,
              updatedAt: new Date().toISOString(),
            }
          : t,
      ),
    );
  }, []);

  const createTemplate = useCallback(
    (
      template: Omit<
        RecurringTemplate,
        "id" | "createdAt" | "updatedAt" | "runCount"
      >,
    ): RecurringTemplate => {
      const now = new Date().toISOString();
      const created: RecurringTemplate = {
        ...template,
        id: `rec_${Date.now().toString(36)}`,
        runCount: 0,
        createdAt: now,
        updatedAt: now,
      };
      setData((prev) => [...prev, created]);
      return created;
    },
    [],
  );

  return {
    data,
    loading,
    error,
    refetch,
    updateTemplate,
    deleteTemplate,
    togglePause,
    runNow,
    skipNext,
    endTemplate,
    createTemplate,
  };
}